import React from 'react'
import classnames from 'classnames'
import { FullBlockLoader, CircleLoader } from '@deal/components'
import SectionHeader from '#src/components/SectionHeader'
import Loader from '#src/routes/categories/components/Results/Loader'
import SellableCard from '#src/components/SellableCard'
import PageContent from '#src/components/PageContent'
import Waypoint from 'react-waypoint'
import {
  InfiniteScrollSellablesComponent,
  SellableType,
  SellableSearchFilter
} from '#src/generated/graphql'
import styles from './styles.css'

interface InfiniteScrollState {
  paginating: boolean
  initialLoadTriggered: boolean
}

interface InfiniteScrollProps {
  id: string
}

class InfiniteScroll extends React.Component<InfiniteScrollProps, InfiniteScrollState> {
  constructor(props: InfiniteScrollProps) {
    super(props)

    this.state = {
      paginating: false,
      initialLoadTriggered: false
    }

    this.handleInitialLoad = this.handleInitialLoad.bind(this)
  }

  public handleInitialLoad() {
    this.setState({
      initialLoadTriggered: true
    })
  }

  public render() {
    const { id } = this.props
    const { paginating, initialLoadTriggered } = this.state

    if (!initialLoadTriggered) {
      return (
        <>
          <Waypoint onEnter={this.handleInitialLoad} />
          <div className={styles.loading}>
            <Loader label="Loading more products" />
          </div>
        </>
      )
    }

    return (
      <InfiniteScrollSellablesComponent
        variables={{ categoryIds: [id], filters: [SellableSearchFilter.EXCLUDE_OUT_OF_STOCK] }}
      >
        {({ data, loading, fetchMore }) => {
          if (loading) {
            return (
              <section className={styles.sectionInfiniteScroll}>
                <FullBlockLoader loader={CircleLoader} />
              </section>
            )
          } else if (data && data.searchSellables.edges.length) {
            const { pageInfo, edges } = data.searchSellables
            const customFetchMore = () => {
              const cursor = data.searchSellables.pageInfo.endCursor!

              this.setState({
                paginating: true
              })

              fetchMore({
                variables: {
                  after: cursor
                },
                updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                  this.setState({
                    paginating: false
                  })

                  if (!fetchMoreResult) {
                    return previousQueryResult
                  }

                  const existingIds = new Set(
                    previousQueryResult.searchSellables.edges.map(edge => edge.node.id)
                  )
                  const newEdges = fetchMoreResult.searchSellables.edges.filter(
                    edge => !existingIds.has(edge.node.id)
                  )
                  const pageInfo = fetchMoreResult.searchSellables.pageInfo!

                  // Append the new results to the results list and update `pageInfo`
                  // so we have the new `endCursor` and `hasNextPage` values
                  return newEdges.length
                    ? {
                        __typename: previousQueryResult.__typename,
                        searchSellables: {
                          __typename: previousQueryResult.searchSellables.__typename,
                          count: fetchMoreResult.searchSellables.count,
                          edges: [...previousQueryResult.searchSellables.edges, ...newEdges],
                          pageInfo
                        }
                      }
                    : previousQueryResult
                }
              })
            }
            return (
              <section className={styles.sectionInfiniteScroll}>
                <PageContent>
                  <div className={styles.sectionBorder}>
                    <SectionHeader header="Recommended for you" />
                    <div className={styles.results}>
                      <ul
                        className={classnames(styles.grid, {
                          [styles.experiences]: edges[0].node.type === SellableType.SERVICE,
                          [styles.products]: edges[0].node.type === SellableType.PRODUCT,
                          [styles.complete]: !pageInfo.hasNextPage
                        })}
                      >
                        {edges.map((result, index) => (
                          <SellableCard
                            sellable={result.node}
                            position={index + 1}
                            className={styles.result}
                            key={result.cursor}
                          />
                        ))}
                        {pageInfo.hasNextPage && !loading ? (
                          <Waypoint key={pageInfo.endCursor!} onEnter={customFetchMore} />
                        ) : null}
                      </ul>
                    </div>
                    {paginating ? (
                      <div className={styles.loading}>
                        <Loader label="Loading more products" />
                      </div>
                    ) : null}
                  </div>
                </PageContent>
              </section>
            )
          }
          return null
        }}
      </InfiniteScrollSellablesComponent>
    )
  }
}

export default InfiniteScroll
